import { type RefObject, useCallback, useRef } from "react";

// DOM-lifetime utilities for pinning a floating preview open while its
// native context menu is up — shared machinery for the explorer's hover
// preview cards (see SchemaPreview).

// After the context menu closes, the pointer may already sit outside the
// card with no further leave event coming — the next mouse move decides:
// inside the card or row keeps the preview, anywhere else closes it.
function closeUnlessHovering(
  rowRef: RefObject<HTMLDivElement | null>,
  close: () => void,
) {
  const onMove = (e: MouseEvent) => {
    document.removeEventListener("mousemove", onMove);
    const card = document.querySelector('[data-testid="schema-preview-card"]');
    const target = e.target as Node;
    if (card?.contains(target) || rowRef.current?.contains(target)) return;
    close();
  };
  document.addEventListener("mousemove", onMove);
}

// Watches the native menu's DOM lifetime (the dropdown exposes no close
// callback and its item clicks don't bubble): once the menu element is
// gone — item picked, outside click or Escape — `released` runs. A menu
// that never appears releases after the ~1s grace period.
function watchMenuLifetime(released: () => void) {
  let sawMenu = false;
  let polls = 0;
  const timer = window.setInterval(() => {
    polls += 1;
    if (document.querySelector('[role="menu"]')) {
      sawMenu = true;
      return;
    }
    if (!sawMenu && polls < 10) return;
    window.clearInterval(timer);
    released();
  }, 100);
}

// While a suite Delete flow is up — its context MENU or its confirmation
// modal — NO preview may open anywhere in the tree: hovering other rows
// must not pop cards over either. A counter, not a flag: the menu's
// lifetime watcher and the modal overlap when Delete is clicked, and the
// menu's release must not clobber the modal's suppression.
let previewSuppressors = 0;
export function acquirePreviewSuppression() {
  previewSuppressors += 1;
}
export function releasePreviewSuppression() {
  previewSuppressors = Math.max(0, previewSuppressors - 1);
}
export function previewsAreSuppressed() {
  return previewSuppressors > 0;
}

// While the primary button is held on the tree — the start of a drag
// gesture — no preview may open: a press-and-hold that outlasts the hover
// delay would otherwise float a card right as the drag begins. Tracked by
// the explorer's pointer listeners (usePreviewDragGuard); a drag's end
// clears it too, since the button's release never reaches the page as a
// pointerup once a native drag has taken over.
let primaryHeld = false;
export function setPrimaryButtonHeld(held: boolean) {
  primaryHeld = held;
}
export function primaryButtonHeld() {
  return primaryHeld;
}

/** Suppresses previews for one context menu's DOM lifetime (the dropdown
 *  exposes no close callback) — call on the row's contextmenu event. */
export function suppressPreviewsWhileMenuOpen() {
  acquirePreviewSuppression();
  watchMenuLifetime(releasePreviewSuppression);
}

// While the context menu is up, the hover interaction must not close the
// preview (the pointer moves onto the menu, which sits outside the card).
export function useContextMenuPin(
  rowRef: RefObject<HTMLDivElement | null>,
  setOpen: (open: boolean) => void,
) {
  const menuPinnedRef = useRef(false);
  const pinForContextMenu = useCallback(() => {
    if (menuPinnedRef.current) return;
    menuPinnedRef.current = true;
    watchMenuLifetime(() => {
      menuPinnedRef.current = false;
      closeUnlessHovering(rowRef, () => setOpen(false));
    });
  }, [rowRef, setOpen]);
  return { menuPinnedRef, pinForContextMenu };
}
